/**
 * RevenueCat Web Billing checkout (backed by Stripe).
 *
 * The public SDK key + the Convex user id (as appUserID) identify the buyer; on
 * a successful purchase RevenueCat fires a webhook to Convex which writes the
 * entitlement (see convex/billing.ts). We then return so the caller can refresh
 * entitlement state and let the user deploy.
 *
 * Products are matched by RevenueCat *package identifier*. Configure these in
 * the RevenueCat dashboard to match: "year_pass", "lifetime", and the
 * owner-only "test_pass".
 */

import { mountCheckoutOverlay } from './checkoutOverlay'

export type PassId = 'year_pass' | 'lifetime' | 'test_pass'

const RC_KEY = import.meta.env.VITE_REVENUECAT_PUBLIC_KEY || ''

const PACKAGE_ALIASES: Record<PassId, string[]> = {
  year_pass: ['year_pass', '$rc_annual', 'annual', 'yearly', 'year', 'pass'],
  lifetime: ['lifetime', '$rc_lifetime'],
  test_pass: ['test_pass'],
}

interface RcPackage {
  identifier?: string
  webBillingProduct?: { identifier?: string }
  product?: { identifier?: string }
}
interface RcPurchases {
  getOfferings: () => Promise<{ current?: { availablePackages?: RcPackage[] } }>
  purchase: (opts: { rcPackage: RcPackage; htmlTarget?: HTMLElement }) => Promise<unknown>
}

let configuredFor: string | null = null
// The SDK instance is created lazily and cached.
let purchasesInstance: RcPurchases | null = null

async function getPurchases(appUserId: string): Promise<RcPurchases> {
  const { Purchases } = await import('@revenuecat/purchases-js')
  if (!purchasesInstance || configuredFor !== appUserId) {
    purchasesInstance = Purchases.configure(RC_KEY, appUserId) as unknown as RcPurchases
    configuredFor = appUserId
  }
  return purchasesInstance
}

export function isBillingConfigured(): boolean {
  return Boolean(RC_KEY)
}

/**
 * Start checkout for a pass. Resolves once the purchase completes. Throws if the
 * user cancels or checkout fails.
 */
export async function purchasePass(appUserId: string, passId: PassId): Promise<void> {
  if (!RC_KEY) throw new Error('Billing is not configured (VITE_REVENUECAT_PUBLIC_KEY missing).')
  const purchases = await getPurchases(appUserId)

  const offerings = await purchases.getOfferings()
  const packages: RcPackage[] = offerings.current?.availablePackages ?? []

  const aliases = PACKAGE_ALIASES[passId]
  const pkg = packages.find((p) => {
    const identifiers = [p.identifier, p.webBillingProduct?.identifier, p.product?.identifier]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase())
    return identifiers.some((identifier) => aliases.some((alias) => identifier.includes(alias.toLowerCase())))
  })
  if (!pkg) throw new Error(`No RevenueCat package found for "${passId}".`)

  const checkoutOverlay = mountCheckoutOverlay(document)
  try {
    await purchases.purchase({ rcPackage: pkg, htmlTarget: checkoutOverlay.target })
  } finally {
    checkoutOverlay.remove()
  }
}
